import { useEffect, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material"
import { APIError, unknownError } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { useAppDispatch } from "../../../app/hooks"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { RichTextEditor } from "../../../app/RichTextEditor"
import {
    isMeetingMinuteContent,
    loadOperationalDashboardAreaConfiguration,
    OperationalDashboardAreaConfigurationDetailsDto,
    updateOperationalDashboardAreaContentText,
} from "../operationalDashboards"

type OperationalDashboardContentDialogProps = {
    dashboardId: number
    areaId: number
    contentId: number
    contentName: string
    onClose: () => void
    onSaved: () => void
}

export const OperationalDashboardContentDialog = ({
    dashboardId, areaId, contentId, contentName, onClose, onSaved,
}: OperationalDashboardContentDialogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const [configuration, setConfiguration] = useState<OperationalDashboardAreaConfigurationDetailsDto | null>(null)
    const [description, setDescription] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [serverError, setServerError] = useState<APIError | null>(null)
    const [retry, setRetry] = useState(0)

    useEffect(() => {
        let active = true
        setIsLoading(true)
        setConfiguration(null)
        setServerError(null)
        dispatch(loadOperationalDashboardAreaConfiguration({ dashboardId, areaId })).then(result => {
            if (!active) return
            if (loadOperationalDashboardAreaConfiguration.fulfilled.match(result)) {
                const loaded = result.payload
                setConfiguration(loaded)
                setDescription(loaded.contents.find(content => content.id === contentId)?.description ?? "")
            } else {
                setServerError(result.payload ?? unknownError())
            }
            setIsLoading(false)
        })
        return () => { active = false }
    }, [dispatch, dashboardId, areaId, contentId, retry])

    const content = configuration?.contents.find(item => item.id === contentId)
    const canSave = !!content && isMeetingMinuteContent(content)

    const handleSave = async () => {
        if (!canSave || isSaving) return
        setIsSaving(true)
        setServerError(null)
        const result = await dispatch(updateOperationalDashboardAreaContentText({
            dashboardId,
            areaContentId: contentId,
            dto: { text: description },
        }))
        if (updateOperationalDashboardAreaContentText.fulfilled.match(result) && result.payload === true) {
            onSaved()
        } else {
            setServerError(updateOperationalDashboardAreaContentText.rejected.match(result)
                ? result.payload ?? unknownError() : unknownError())
            setIsSaving(false)
        }
    }

    return (
        <Dialog open onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md"
            aria-labelledby="operational-dashboard-content-dialog-title">
            <DialogTitle id="operational-dashboard-content-dialog-title" sx={{ bgcolor: "grey.200", color: "text.primary", fontWeight: "bold" }}>
                <Typography component="span" fontWeight="bold">
                    {l10n.getString("edit", null, "_Edytuj")}: {content?.name ?? contentName}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {serverError && (
                    <Alert sx={{ mb: 2 }} onClose={() => setServerError(null)} severity="error">
                        {serverError.kind === "http" && serverError.problem ? (
                            <Localized id={serverError.problem.title}>
                                <span>{serverError.problem.detail || serverError.problem.title}</span>
                            </Localized>
                        ) : serverError.kind === "connection" ? (
                            <Localized id="connection-issue">_Problem z połączeniem</Localized>
                        ) : serverError.kind === "http" ? (
                            `${l10n.getString("error", null, "_Błąd")} (HTTP ${serverError.status})`
                        ) : (
                            <Localized id="error-unknown">_Wystąpił nieoczekiwany problem</Localized>
                        )}
                    </Alert>
                )}
                {isLoading ? <LoadingContainer /> : configuration && content && canSave ? (
                    <Box sx={{ pt: 1 }}>
                        <RichTextEditor label={l10n.getString("description", null, "_Opis")}
                            value={description} onValueChange={setDescription} editable={!isSaving} />
                    </Box>
                ) : configuration ? (
                    <Alert severity="error">
                        {l10n.getString("operational-dashboard-content-edit-unavailable", null, "_Nie można edytować tej treści w tym kafelku.")}
                    </Alert>
                ) : (
                    <Button onClick={() => setRetry(value => value + 1)}>
                        {l10n.getString("retry", null, "_Ponów")}
                    </Button>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={isSaving} disabled={isLoading || !canSave} onClick={handleSave} />
                <CancelButton loading={isSaving} disabled={isSaving} onClick={onClose} />
            </DialogActions>
        </Dialog>
    )
}
